// Ephemeral local diagnostics; preferences live in the personal settings API.
export type LaneTiming = {
  endOfSpeechMs: number | null;
  undoMs: number | null;
  modelToTextMs: number | null;
  toolMs: number | null;
  ttsToAudioMs: number | null;
  totalMs: number | null;
  firstAudioMs: number | null;
  partGapMs: number | null;
  maxPartGapMs: number | null;
  playbackProfile: PlaybackProfile | null;
};
export type PlaybackProfile = { provider: string; model: string; voiceId: string; name: string };
export type LaneTimingState = { enabled: boolean; latest: LaneTiming | null };

const listeners = new Set<() => void>();
let enabled = false;
let latest: LaneTiming | null = null;
let snapshot: LaneTimingState = { enabled, latest };
let spokenTextAt: number | null = null;
let speechEndedAt: number | null = null;

const notify = () => {
  snapshot = { enabled, latest };
  listeners.forEach((listener) => listener());
};
const finite = (value: number | null | undefined) =>
  Number.isFinite(value) && (value as number) >= 0 ? (value as number) : null;

export function laneTimingEnabled(value: unknown): boolean {
  return value === true;
}

export function laneTimingState() {
  return snapshot;
}

export function setLaneTimingEnabled(value: boolean) {
  enabled = value;
  if (!enabled) {
    latest = null;
    spokenTextAt = null;
    speechEndedAt = null;
  }
  notify();
}

export function subscribeLaneTiming(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function publishLaneTiming(input: Partial<LaneTiming>) {
  if (!enabled) return;
  latest = {
    endOfSpeechMs:
      input.endOfSpeechMs === undefined
        ? (latest?.endOfSpeechMs ?? null)
        : finite(input.endOfSpeechMs),
    undoMs: input.undoMs === undefined ? (latest?.undoMs ?? null) : finite(input.undoMs),
    modelToTextMs:
      input.modelToTextMs === undefined
        ? (latest?.modelToTextMs ?? null)
        : finite(input.modelToTextMs),
    toolMs: input.toolMs === undefined ? (latest?.toolMs ?? null) : finite(input.toolMs),
    ttsToAudioMs:
      input.ttsToAudioMs === undefined
        ? (latest?.ttsToAudioMs ?? null)
        : finite(input.ttsToAudioMs),
    totalMs: input.totalMs === undefined ? (latest?.totalMs ?? null) : finite(input.totalMs),
    firstAudioMs:
      input.firstAudioMs === undefined
        ? (latest?.firstAudioMs ?? null)
        : finite(input.firstAudioMs),
    partGapMs:
      input.partGapMs === undefined ? (latest?.partGapMs ?? null) : finite(input.partGapMs),
    maxPartGapMs:
      input.maxPartGapMs === undefined
        ? (latest?.maxPartGapMs ?? null)
        : finite(input.maxPartGapMs),
    playbackProfile:
      input.playbackProfile === undefined
        ? (latest?.playbackProfile ?? null)
        : input.playbackProfile,
  };
  notify();
}

export function clearLaneTiming() {
  latest = null;
  spokenTextAt = null;
  speechEndedAt = null;
  notify();
}

export function markLaneSpokenText(at = performance.now()) {
  if (!enabled) return;
  spokenTextAt = Number.isFinite(at) ? at : null;
}

export function laneSpokenTextAt() {
  return spokenTextAt;
}

export function markLaneSpeechEnded(at: number | null) {
  if (!enabled) return;
  speechEndedAt = Number.isFinite(at) ? at : null;
}

export function laneSpeechEndedAt() {
  return speechEndedAt;
}
